import React, { useState, useEffect } from 'react';
import { Home, Search, Heart, MessageSquare, MapPin, PoundSterling, Clock, CheckCircle2, AlertCircle, ChevronRight, Bookmark, ExternalLink } from 'lucide-react';
import DashboardMessaging from './DashboardMessaging';

interface Property {
    id: string;
    title: string;
    location: string;
    price: string;
    type: string;
    commuteTime: number;
    images?: string[];
    isStudentFriendly?: boolean;
    tags?: string[];
    sourceLink?: string;
}

const AccommodationManager: React.FC<{ onBack?: () => void }> = ({ onBack }) => {
    const [matches, setMatches] = useState<Property[]>([]);
    const [shortlist, setShortlist] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState<string | null>(null);

    const fetchData = async () => {
        try {
            const token = localStorage.getItem('token');
            // For now, we reuse the prediction API but for paid users we might have a stored state
            // Let's check for stored state (Step 1)
            const stateRes = await fetch('/api/student/accommodation/state', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const stateData = await stateRes.json();

            if (stateData && stateData.Data) {
                // If we have preferences, trigger the match
                // For demonstration, let's trigger a match or fetch shortlist
                const shortlistRes = await fetch('/api/student/accommodation/shortlist', {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const shortlistData = await shortlistRes.json();
                setShortlist(shortlistData || []);

                // Trigger or Fetch Properties (Mocking prediction call for speed)
                // In production, this would call /api/predict-accommodation with the saved data
                const matchRes = await fetch('/api/predict-accommodation', {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: stateData.Data
                });
                const matchData = await matchRes.json();
                setMatches([...(matchData.topMatches || []), ...(matchData.additionalMatches || [])]);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleShortlist = async (prop: Property) => {
        setSaving(prop.id);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch('/api/student/accommodation/shortlist', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    PropertyID: prop.id,
                    Title: prop.title,
                    Price: prop.price,
                    SourceLink: prop.sourceLink || "",
                })
            });
            if (res.ok) {
                fetchData();
            }
        } catch (err) {
            console.error(err);
        } finally {
            setSaving(null);
        }
    };

    if (loading) return <div className="text-[#009B8F] font-bold p-10 text-center animate-pulse">Scanning Premium Listings...</div>;

    return (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            <div className="lg:col-span-3 space-y-8">
                {/* Header Banner */}
                <div className="bg-[#011E26] rounded-2xl p-8 relative overflow-hidden shadow-lg">
                    <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-6">
                        <div>
                            <div className="inline-flex items-center gap-2 bg-[#009B8F]/20 px-3 py-1 rounded border border-[#009B8F]/30 mb-3">
                                <div className="w-1.5 h-1.5 rounded-full bg-[#009B8F] animate-pulse"></div>
                                <span className="text-[10px] font-bold tracking-widest text-[#009B8F] uppercase">AI Matched</span>
                            </div>
                            <h2 className="text-3xl font-serif font-bold text-white mb-1">Premium Matches</h2>
                            <p className="text-slate-400 text-sm">Nex-AI curated listings — 15 min walk requirement</p>
                        </div>
                        <div className="bg-white/10 border border-white/10 px-5 py-3 rounded-xl flex items-center gap-3">
                            <Heart size={18} className="fill-[#009B8F] text-[#009B8F]" />
                            <span className="font-bold text-white text-sm uppercase tracking-widest">{shortlist.length} Shortlisted</span>
                        </div>
                    </div>
                    <div className="absolute top-0 right-0 w-48 h-48 bg-[#009B8F]/10 rounded-full -mr-12 -mt-12 pointer-events-none"></div>
                </div>

                {/* Property Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {matches.map((prop) => {
                        const isShortlisted = shortlist.some(s => s.PropertyID === prop.id);
                        return (
                            <div key={prop.id} className="group bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-md hover:border-[#009B8F]/30 transition-all duration-300">
                                <div className="h-48 relative overflow-hidden">
                                    <img
                                        src={`https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&q=80&w=600`}
                                        alt={prop.title}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                    />
                                    <div className="absolute top-3 right-3 bg-white/95 px-3 py-1.5 rounded-lg shadow-sm">
                                        <span className="text-xs font-bold text-slate-800">{prop.price}</span>
                                    </div>
                                    <div className="absolute bottom-3 left-3 flex gap-1.5">
                                        {prop.tags?.slice(0, 2).map(tag => (
                                            <span key={tag} className="bg-[#009B8F] text-white text-[8px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wide">
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                <div className="p-5">
                                    <h4 className="font-bold text-slate-800 text-base mb-1">{prop.title}</h4>
                                    <p className="text-xs text-slate-500 flex items-center gap-1.5 mb-3">
                                        <MapPin size={11} className="text-[#009B8F]" /> {prop.location}
                                    </p>

                                    {prop.sourceLink && (
                                        <a href={prop.sourceLink} target="_blank" rel="noopener noreferrer" className="text-xs text-[#009B8F] font-bold hover:underline flex items-center gap-1 mb-3">
                                            <ExternalLink size={11} /> View Original Listing
                                        </a>
                                    )}

                                    <div className="flex justify-between items-center pt-4 border-t border-gray-100">
                                        <div className="flex items-center gap-4">
                                            <div>
                                                <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider block mb-0.5">Commute</span>
                                                <span className="text-xs text-slate-700 font-bold flex items-center gap-1">
                                                    <Clock size={11} className="text-[#009B8F]" /> {prop.commuteTime}m Walk
                                                </span>
                                            </div>
                                            {prop.isStudentFriendly && (
                                                <div>
                                                    <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider block mb-0.5">Status</span>
                                                    <span className="text-xs text-emerald-600 font-bold flex items-center gap-1">
                                                        <CheckCircle2 size={11} /> Verified
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                        <button
                                            disabled={isShortlisted || saving === prop.id}
                                            onClick={() => handleShortlist(prop)}
                                            className={`w-10 h-10 rounded-xl transition-all flex items-center justify-center ${
                                                isShortlisted
                                                    ? 'bg-[#009B8F] text-white shadow-sm shadow-[#009B8F]/20'
                                                    : 'bg-gray-50 text-slate-400 hover:bg-[#009B8F]/10 hover:text-[#009B8F] border border-gray-200'
                                            }`}
                                        >
                                            {isShortlisted ? <CheckCircle2 size={18} /> : <Bookmark size={18} />}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1 space-y-6">
                <div className="bg-white border border-gray-100 shadow-sm p-5 rounded-2xl flex flex-col gap-4">
                    <h3 className="text-base font-serif font-bold text-slate-800 flex items-center gap-2">
                        <MessageSquare size={18} className="text-[#009B8F]" /> Expert Support
                    </h3>
                    <DashboardMessaging context="accommodation" />
                </div>

                <div className="bg-white border border-gray-100 shadow-sm p-6 rounded-2xl">
                    <h3 className="font-serif font-bold text-slate-800 text-base mb-4">Next Goal</h3>
                    <div className="p-4 bg-[#009B8F]/5 rounded-xl border border-[#009B8F]/20">
                        <p className="text-xs text-slate-600 leading-relaxed">
                            Once you have shortlisted 3–5 properties, our booking agent will contact the landlords to arrange virtual viewings.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AccommodationManager;
